/// <reference types="vite/client" />
import { Canvas } from '@react-three/fiber';
import { View } from '@react-three/drei';
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';

const ViewportHostContext = createContext(false);

/**
 * True when rendered beneath a `<ViewportHost>`. Every `<Viewport>` (see
 * ./Viewport.tsx) checks this to decide whether to project into the shared
 * canvas or fall back to owning its own.
 */
export function useViewportHost(): boolean {
  return useContext(ViewportHostContext);
}

export interface ViewportHostProps {
  children: ReactNode;
}

/**
 * Mounts the single shared WebGL context that every `<Viewport>` nested
 * beneath it projects into via `@react-three/drei`'s `<View>`
 * (gl.scissor-based multi-viewport rendering) - see
 * docs/editor-consolidation/roadmap.md section 3.3. Chrome caps live WebGL
 * contexts at roughly 16/tab and silently kills the oldest past that;
 * without this, each viewport-owning editor route mounting its own
 * `<Canvas>` risks exhausting that cap once panel-based layouts (a separate
 * branch) let several coexist. `screens-editor.tsx` wraps its whole
 * mode-rail dispatch region in one of these.
 *
 * The canvas itself stays fixed to the full viewport and non-interactive at
 * the DOM level - `<Canvas>` sets its own wrapping element to
 * `pointer-events: none` automatically once a custom `eventSource` is
 * supplied (see `@react-three/fiber`'s `CanvasImpl`). Every visible,
 * interactive viewport is actually a `<Viewport>`'s own normal-flow
 * tracking element living elsewhere in the page; `<View>` scissors this
 * canvas's draw calls into that element's real screen rect every frame.
 * `<View>`'s scissor math is canvas-relative (it subtracts the canvas's own
 * `size.left`/`size.top`, sourced from the real `getBoundingClientRect()`
 * of its container - see `react-use-measure` inside
 * `@react-three/fiber`'s `Canvas`), so a viewport docked inside the
 * editor's chrome (not full-screen) still renders and hit-tests correctly.
 */
export function ViewportHost({ children }: ViewportHostProps) {
  // Non-null initial value (see Viewport.tsx's own trackRef) so this
  // matches `eventSource`'s `RefObject<HTMLElement>` (no `| null`).
  const hostRef = useRef<HTMLDivElement>(null!);
  // The actual shared <canvas> DOM element (R3F's `ref` forwards to it, not
  // to either of its own wrapping divs - see CanvasImpl) - only used by the
  // dev-mode origin check below.
  const canvasElRef = useRef<HTMLCanvasElement>(null);

  // Dev-only guard against silently reintroducing drei#2223 (see
  // docs/editor-consolidation/roadmap.md section 3.3): that bug's
  // `isOffscreen` gate never compensates for the shared canvas's own
  // top/left offset, so a genuinely on-screen panel can render blank with no
  // console error the moment this element stops sitting at the page origin.
  // Today that's mathematically unreachable because the canvas is pinned to
  // `position: fixed; inset: 0` above, so this only ever fires if some
  // future change accidentally sizes or offsets it - checked once after
  // mount (not on every resize; a persistent regression will still be at a
  // non-zero offset whenever this runs), and stripped from production
  // builds since it is diagnostic only, not a runtime safeguard.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const rect = canvasElRef.current?.getBoundingClientRect();
    if (rect && (rect.top !== 0 || rect.left !== 0)) {
      console.warn(
        `[ViewportHost] shared canvas is not at the page origin (top=${rect.top}, left=${rect.left}). ` +
          "This makes drei#2223's isOffscreen bug reachable again - a docked panel that is genuinely " +
          'on-screen can render silently blank. The canvas must stay pinned to position: fixed; inset: 0.',
      );
    }
  }, []);

  return (
    <ViewportHostContext.Provider value={true}>
      {/* `display: contents` keeps this wrapper invisible to layout, so
          `children` (the active route) stays a direct flex item of
          screens-editor.tsx's own layout, exactly as before this wrap. */}
      <div ref={hostRef} style={{ display: 'contents' }}>
        <Canvas
          ref={canvasElRef}
          shadows
          gl={{ antialias: true }}
          eventSource={hostRef}
          style={{ position: 'fixed', inset: 0, zIndex: -1 }}
        >
          <View.Port />
        </Canvas>
        {children}
      </div>
    </ViewportHostContext.Provider>
  );
}
